// InstallPromptBanner — RESPONSIVE_DESIGN.md §20.2
//
// Three responsive variants:
//   • lg+ — top gradient banner (48 px) for POS terminals
//   • md — bottom glass sheet with icon + description
//   • xs-sm — compact top strip (40 px) that only appears after the 3rd visit
//
// All variants use @bbpos/ui primitives and the GLASS design tokens from
// apps/pos/src/constants/design-tokens.ts. The banner self-hides when the
// app is running in standalone mode (via the hook's matchMedia listener)
// and when the user recently dismissed it.

import { useState } from 'react';
import { Button } from '@bbpos/ui';
import { useInstallPrompt } from '@/hooks/use-install-prompt';
import { cn } from '@/constants/design-tokens';

// Minimum visits before the compact xs-sm strip surfaces. Matches
// RESPONSIVE_DESIGN.md §20.2 — "After 3rd visit (engagement heuristic)".
const XS_SM_MIN_VISITS = 3;

function SparkIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v4M12 17v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M3 12h4M17 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function CloseIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function InstallPromptBanner() {
  const { canInstall, visitCount, install, dismiss } = useInstallPrompt();
  const [busy, setBusy] = useState(false);

  if (!canInstall) return null;

  const handleInstall = async () => {
    setBusy(true);
    try {
      await install();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* ═════ lg+  top banner (POS terminal) ═════ */}
      <div
        className={cn(
          'pwa-install-banner',
          'fixed top-0 left-0 right-0 z-40 hidden lg:block',
          'bg-gradient-to-r from-brand-600/95 via-indigo-600/90 to-violet-600/95',
          'backdrop-blur-md text-white shadow-[0_12px_30px_-18px_rgba(67,56,202,0.7)]',
          'animate-slide-down',
        )}
        role="dialog"
        aria-label="Install Orio360"
      >
        <div className="max-w-6xl mx-auto flex items-center gap-4 px-5 h-12">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20">
            <SparkIcon className="h-4 w-4 text-amber-200" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              Install Orio360 for full-screen billing
            </p>
            <p className="text-[11px] text-indigo-100/90 leading-tight">
              Launch instantly from the home screen, stay full-screen, and keep working offline.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleInstall}
            disabled={busy}
            className="!bg-white !text-brand-700 hover:!bg-indigo-50"
          >
            {busy ? 'Installing…' : 'Install'}
          </Button>
          <button
            type="button"
            onClick={dismiss}
            className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Dismiss install prompt"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* ═════ md — bottom glass sheet (tablet) ═════ */}
      <div
        className={cn(
          'pwa-install-banner',
          'fixed bottom-4 left-4 right-4 z-40 hidden md:block lg:hidden',
          'rounded-2xl bg-white/85 dark:bg-neutral-900/85 backdrop-blur-md',
          'shadow-[0_24px_70px_-24px_rgba(15,23,42,0.45)]',
          'animate-slide-up',
        )}
        role="dialog"
        aria-label="Add Orio360 to Home Screen"
      >
        <div className="flex items-center gap-4 p-5">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white shadow-lg shadow-brand-500/30 shrink-0">
            <SparkIcon className="h-7 w-7 text-amber-200" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-neutral-900 dark:text-neutral-100 leading-tight">
              Add Orio360 to Home Screen
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 leading-snug">
              Fast launch, offline billing, and push alerts — right from your home screen.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="primary" size="sm" onClick={handleInstall} disabled={busy}>
              {busy ? 'Installing…' : 'Install'}
            </Button>
            <button
              type="button"
              onClick={dismiss}
              className="p-2 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              aria-label="Dismiss install prompt"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>

      {/* ═════ xs-sm — compact top strip (after 3rd visit) ═════ */}
      {visitCount >= XS_SM_MIN_VISITS && (
        <div
          className={cn(
            'pwa-install-banner',
            'fixed top-0 left-0 right-0 z-40 md:hidden',
            'bg-gradient-to-r from-brand-600 to-indigo-600 text-white',
            'shadow-[0_8px_24px_-16px_rgba(67,56,202,0.7)]',
            'animate-slide-down',
          )}
          role="dialog"
          aria-label="Install Orio360"
        >
          <div className="flex items-center gap-2 h-10 px-3 text-xs">
            <SparkIcon className="h-3.5 w-3.5 text-amber-200 shrink-0" />
            <span className="flex-1 min-w-0 font-medium truncate">Install Orio360</span>
            <button
              type="button"
              onClick={handleInstall}
              disabled={busy}
              className="font-bold underline underline-offset-2 disabled:opacity-50"
            >
              {busy ? '…' : 'Install'}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="p-1 rounded-full text-white/80 hover:text-white hover:bg-white/10"
              aria-label="Dismiss install prompt"
            >
              <CloseIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
